import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { useNavigate } from 'react-router-dom';
import { API, getAuthToken } from '../App';
import { Button } from '../components/ui/button';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../components/ui/dialog';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Textarea } from '../components/ui/textarea';
import { toast } from 'sonner';
import { ArrowLeft, Plus, FileText, Link2, Eye, Trash2, Edit, Copy, Users, Send, Download } from 'lucide-react';
import { Badge } from '../components/ui/badge';
import { format } from 'date-fns';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Switch } from '../components/ui/switch';

const Forms = () => {
  const navigate = useNavigate();
  const [currentUser, setCurrentUser] = useState(null);
  const [forms, setForms] = useState([]);
  const [selectedForm, setSelectedForm] = useState(null);
  const [formResponses, setFormResponses] = useState([]);
  const [isCreateDialogOpen, setIsCreateDialogOpen] = useState(false);
  const [isResponsesDialogOpen, setIsResponsesDialogOpen] = useState(false);
  const [responseSearchQuery, setResponseSearchQuery] = useState('');
  const [statusReasonDialog, setStatusReasonDialog] = useState({ open: false, responseId: null, newStatus: null });
  const [statusReason, setStatusReason] = useState('');
  
  const [newForm, setNewForm] = useState({
    title: '',
    description: '',
    fields: []
  });
  
  const [newField, setNewField] = useState({
    label: '',
    field_type: 'text',
    required: false,
    placeholder: '',
    options: ''
  });

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const token = getAuthToken();
      if (!token) {
        navigate('/login');
        return;
      }
      
      const config = { headers: { Authorization: `Bearer ${token}` } };
      const [userRes, formsRes] = await Promise.all([
        axios.get(`${API}/auth/me`, config),
        axios.get(`${API}/forms`, config)
      ]);
      
      setCurrentUser(userRes.data);
      setForms(formsRes.data);
      
      // Check if user has access
      if (!['admin', 'crm_user'].includes(userRes.data.role)) {
        toast.error('Access denied - CRM Users only');
        navigate('/');
      }
    } catch (error) {
      if (error.response?.status === 401) {
        navigate('/login');
      }
      toast.error('Failed to load data');
    }
  };

  const handleAddField = () => {
    if (!newField.label) {
      toast.error('Field label is required');
      return;
    }
    
    const field = {
      id: `field_${Date.now()}`,
      label: newField.label,
      field_type: newField.field_type,
      required: newField.required,
      placeholder: newField.placeholder,
      options: newField.field_type === 'select' ? newField.options.split(',').map(o => o.trim()).filter(o => o) : null
    };
    
    setNewForm({
      ...newForm,
      fields: [...newForm.fields, field]
    });
    
    setNewField({
      label: '',
      field_type: 'text',
      required: false,
      placeholder: '',
      options: ''
    });
  };

  const handleRemoveField = (fieldId) => {
    setNewForm({
      ...newForm,
      fields: newForm.fields.filter(f => f.id !== fieldId)
    });
  };

  const handleCreateForm = async () => {
    if (!newForm.title) {
      toast.error('Form title is required');
      return;
    }
    
    if (newForm.fields.length === 0) {
      toast.error('Add at least one field to the form');
      return;
    }

    try {
      const token = getAuthToken();
      const config = { headers: { Authorization: `Bearer ${token}` } };
      
      await axios.post(`${API}/forms`, newForm, config);
      toast.success('Form created successfully!');
      
      setIsCreateDialogOpen(false);
      setNewForm({ title: '', description: '', fields: [] });
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to create form');
    }
  };

  const handleViewResponses = async (form) => {
    try {
      const token = getAuthToken();
      const config = { headers: { Authorization: `Bearer ${token}` } };
      
      const res = await axios.get(`${API}/forms/${form.id}/responses`, config);
      setSelectedForm(form);
      setFormResponses(res.data);
      setIsResponsesDialogOpen(true);
    } catch (error) {
      toast.error('Failed to load responses');
    }
  };

  const handleDeleteForm = async (formId) => {
    if (!window.confirm('Are you sure you want to delete this form and all its responses?')) return;
    
    try {
      const token = getAuthToken();
      const config = { headers: { Authorization: `Bearer ${token}` } };
      
      await axios.delete(`${API}/forms/${formId}`, config);
      toast.success('Form deleted!');
      fetchData();
    } catch (error) {
      toast.error('Failed to delete form');
    }
  };

  const handleResponseStatusUpdate = async (responseId, newStatus, reason) => {
    try {
      const token = getAuthToken();
      await axios.patch(`${API}/forms/responses/${responseId}/status`,
        { status: newStatus, status_reason: reason },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setFormResponses(prev => prev.map(r =>
        r.id === responseId ? { ...r, status: newStatus, status_reason: reason } : r
      ));
      toast.success('Status updated');
    } catch (error) {
      toast.error('Failed to update status');
    }
  };

  const copyFormLink = (formId) => {
    const link = `${window.location.origin}/form/${formId}`;
    const textArea = document.createElement('textarea');
    textArea.value = link;
    textArea.style.position = 'fixed';
    textArea.style.left = '-999999px';
    textArea.style.top = '0';
    document.body.appendChild(textArea);
    textArea.focus();
    textArea.select();
    try {
      document.execCommand('copy');
      toast.success('Form link copied to clipboard!');
    } catch (e) {
      toast.error('Could not copy link');
    }
    document.body.removeChild(textArea);
  };

  const exportToCSV = () => {
    if (!selectedForm || !formResponses.length) return;
    
    // Create CSV headers
    const headers = ['#', ...selectedForm.fields.map(f => f.label), 'Submitted At'];
    
    // Create CSV rows
    const rows = formResponses.map((response, index) => {
      const rowData = [
        index + 1,
        ...selectedForm.fields.map(field => {
          const value = response.responses?.[field.id] || '';
          // Escape values with commas or quotes
          if (typeof value === 'string' && (value.includes(',') || value.includes('"'))) {
            return `"${value.replace(/"/g, '""')}"`;
          }
          return value;
        }),
        response.submitted_at ? format(new Date(response.submitted_at), 'yyyy-MM-dd h:mm:ss a') : ''
      ];
      return rowData.join(',');
    });
    
    // Combine headers and rows
    const csv = [headers.join(','), ...rows].join('\n');
    
    // Download file
    const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    link.href = URL.createObjectURL(blob);
    link.download = `${selectedForm.title.replace(/[^a-z0-9]/gi, '_')}_responses.csv`;
    link.click();
    URL.revokeObjectURL(link.href);
    toast.success('Responses exported to CSV!');
  };

  const getFieldTypeLabel = (type) => {
    const types = {
      text: 'Text',
      email: 'Email',
      phone: 'Phone',
      number: 'Number',
      textarea: 'Long Text',
      select: 'Dropdown',
      date: 'Date'
    };
    return types[type] || type;
  };

  return (
    <div className="min-h-screen bg-background">
      <header className="border-b border-border bg-card sticky top-0 z-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-16">
            <div className="flex items-center gap-4">
              <Button
                variant="ghost"
                onClick={() => navigate('/')}
                className="rounded-sm"
              >
                <ArrowLeft className="h-4 w-4 mr-2" />
                Back
              </Button>
              <div>
                <h1 className="font-heading text-xl font-bold">Candidate Forms</h1>
                <p className="text-xs text-muted-foreground">Create and manage candidate intake forms</p>
              </div>
            </div>
            
            <Button 
              onClick={() => setIsCreateDialogOpen(true)}
              className="rounded-sm"
              data-testid="create-form-button"
            >
              <Plus className="h-4 w-4 mr-2" />
              Create Form
            </Button>
          </div>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {forms.length === 0 ? (
          <div className="border border-border rounded-sm p-12 text-center">
            <FileText className="h-12 w-12 text-muted-foreground mx-auto mb-4 opacity-50" />
            <p className="text-muted-foreground">No forms created yet</p>
            <p className="text-sm text-muted-foreground mt-1">Create your first form to collect candidate information</p>
          </div>
        ) : (
          <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3">
            {forms.map((form) => (
              <Card key={form.id} className="rounded-sm">
                <CardHeader className="pb-3">
                  <div className="flex items-start justify-between">
                    <div>
                      <CardTitle className="font-heading text-lg">{form.title}</CardTitle>
                      <CardDescription className="text-xs">
                        {form.fields?.length || 0} fields • Created {form.created_at && format(new Date(form.created_at), 'MMM dd, yyyy')}
                      </CardDescription>
                    </div>
                    <Badge className={form.is_active !== false ? 'bg-green-500' : 'bg-gray-500'}>
                      {form.is_active !== false ? 'Active' : 'Inactive'}
                    </Badge>
                  </div>
                </CardHeader>
                <CardContent>
                  {form.description && (
                    <p className="text-sm text-muted-foreground mb-4">{form.description}</p>
                  )}
                  
                  <div className="flex flex-wrap gap-2">
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => copyFormLink(form.id)}
                      className="rounded-sm"
                    >
                      <Copy className="h-3 w-3 mr-1" />
                      Copy Link
                    </Button>
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => handleViewResponses(form)}
                      className="rounded-sm"
                    >
                      <Eye className="h-3 w-3 mr-1" />
                      Responses
                    </Button>
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => window.open(`/form/${form.id}`, '_blank')}
                      className="rounded-sm"
                    >
                      <Link2 className="h-3 w-3 mr-1" />
                      Preview
                    </Button>
                    <Button
                      variant="destructive"
                      size="sm"
                      onClick={() => handleDeleteForm(form.id)}
                      className="rounded-sm"
                      data-testid={`delete-form-${form.id}`}
                    >
                      <Trash2 className="h-3 w-3" />
                    </Button>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        )}

        {/* Create Form Dialog */}
        <Dialog open={isCreateDialogOpen} onOpenChange={setIsCreateDialogOpen}>
          <DialogContent className="sm:max-w-[700px] max-h-[90vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle className="font-heading">Create New Form</DialogTitle>
            </DialogHeader>
            <div className="space-y-6">
              <div className="space-y-4">
                <div className="space-y-2">
                  <Label>Form Title *</Label>
                  <Input
                    value={newForm.title}
                    onChange={(e) => setNewForm({ ...newForm, title: e.target.value })}
                    placeholder="e.g., Candidate Registration Form"
                    className="rounded-sm"
                  />
                </div>
                <div className="space-y-2">
                  <Label>Description</Label>
                  <Textarea
                    value={newForm.description}
                    onChange={(e) => setNewForm({ ...newForm, description: e.target.value })}
                    placeholder="Brief description of the form"
                    className="rounded-sm"
                    rows={2}
                  />
                </div>
              </div>

              {/* Add Field Section */}
              <div className="border border-border rounded-sm p-4">
                <h4 className="font-heading font-semibold mb-3">Add Field</h4>
                <div className="grid grid-cols-2 gap-3">
                  <div className="space-y-2">
                    <Label>Field Label *</Label>
                    <Input
                      value={newField.label}
                      onChange={(e) => setNewField({ ...newField, label: e.target.value })}
                      placeholder="e.g., Full Name"
                      className="rounded-sm"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label>Field Type</Label>
                    <Select
                      value={newField.field_type}
                      onValueChange={(value) => {
                        const updated = { ...newField, field_type: value };
                        if (['select', 'multiselect', 'checkbox', 'radio'].includes(value) && !newField.options) {
                          updated.options = 'Option 1, Option 2';
                        }
                        setNewField(updated);
                      }}
                    >
                      <SelectTrigger className="rounded-sm">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="text">Text</SelectItem>
                        <SelectItem value="email">Email</SelectItem>
                        <SelectItem value="phone">Phone</SelectItem>
                        <SelectItem value="number">Number</SelectItem>
                        <SelectItem value="textarea">Long Text</SelectItem>
                        <SelectItem value="select">Dropdown</SelectItem>
                        <SelectItem value="multiselect">Multi-Select</SelectItem>
                        <SelectItem value="checkbox">Checkboxes</SelectItem>
                        <SelectItem value="radio">Radio Buttons</SelectItem>
                        <SelectItem value="date">Date</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="space-y-2">
                    <Label>Placeholder</Label>
                    <Input
                      value={newField.placeholder}
                      onChange={(e) => setNewField({ ...newField, placeholder: e.target.value })}
                      placeholder="Helper text"
                      className="rounded-sm"
                    />
                  </div>
                  <div className="flex items-center gap-2 pt-6">
                    <Switch
                      checked={newField.required}
                      onCheckedChange={(checked) => setNewField({ ...newField, required: checked })}
                    />
                    <Label>Required</Label>
                  </div>
                </div>
                
                {['select', 'multiselect', 'checkbox', 'radio'].includes(newField.field_type) && (
                  <div className="space-y-2 mt-3">
                    <Label>Options (comma-separated)</Label>
                    <Input
                      value={newField.options}
                      onChange={(e) => setNewField({ ...newField, options: e.target.value })}
                      placeholder="Option 1, Option 2, Option 3"
                      className="rounded-sm"
                    />
                    <p className="text-xs text-muted-foreground">
                      {newField.field_type === 'checkbox' && 'Users can select multiple options'}
                      {newField.field_type === 'multiselect' && 'Users can select multiple options from dropdown'}
                      {newField.field_type === 'radio' && 'Users can select only one option'}
                      {newField.field_type === 'select' && 'Users can select one option from dropdown'}
                    </p>
                  </div>
                )}
                
                <Button 
                  onClick={handleAddField} 
                  className="mt-3 rounded-sm"
                  variant="outline"
                >
                  <Plus className="h-4 w-4 mr-2" />
                  Add Field
                </Button>
              </div>

              {/* Fields Preview */}
              {newForm.fields.length > 0 && (
                <div className="space-y-2">
                  <Label>Form Fields ({newForm.fields.length})</Label>
                  <div className="border border-border rounded-sm divide-y">
                    {newForm.fields.map((field, index) => (
                      <div key={field.id} className="p-3 flex items-center justify-between">
                        <div>
                          <span className="font-medium">{field.label}</span>
                          {field.required && <span className="text-red-500 ml-1">*</span>}
                          <Badge variant="outline" className="ml-2 text-xs">
                            {getFieldTypeLabel(field.field_type)}
                          </Badge>
                        </div>
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => handleRemoveField(field.id)}
                        >
                          <Trash2 className="h-4 w-4 text-red-500" />
                        </Button>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              <Button 
                onClick={handleCreateForm} 
                className="w-full rounded-sm"
                disabled={!newForm.title || newForm.fields.length === 0}
              >
                Create Form
              </Button>
            </div>
          </DialogContent>
        </Dialog>

        {/* View Responses Dialog - Excel-like View */}
        <Dialog open={isResponsesDialogOpen} onOpenChange={setIsResponsesDialogOpen}>
          <DialogContent className="sm:max-w-[95vw] max-h-[90vh] overflow-hidden flex flex-col">
            <DialogHeader className="flex-shrink-0">
              <div className="flex items-center justify-between">
                <DialogTitle className="font-heading">
                  Responses - {selectedForm?.title}
                </DialogTitle>
                {formResponses.length > 0 && (
                  <Button 
                    variant="outline" 
                    size="sm" 
                    onClick={exportToCSV}
                    className="ml-4"
                  >
                    <Download className="h-4 w-4 mr-2" />
                    Export CSV
                  </Button>
                )}
              </div>
            </DialogHeader>
            
            {formResponses.length === 0 ? (
              <div className="text-center py-8">
                <Users className="h-12 w-12 text-muted-foreground mx-auto mb-4 opacity-50" />
                <p className="text-muted-foreground">No responses yet</p>
                <p className="text-sm text-muted-foreground mt-1">Share the form link with candidates to collect responses</p>
              </div>
            ) : (
              <div className="flex-1 overflow-hidden flex flex-col">
                <p className="text-sm text-muted-foreground mb-3 flex-shrink-0">{formResponses.length} response(s)</p>
                
                {/* Excel-like spreadsheet */}
                <div className="flex-1 border-2 border-gray-300 rounded-lg overflow-auto bg-white">
                  <table className="w-full text-sm border-collapse" style={{ minWidth: 'max-content' }}>
                    <thead className="sticky top-0 z-10">
                      <tr className="bg-gradient-to-r from-green-600 to-green-700 text-white">
                        <th className="text-left p-3 font-bold border-r border-green-500 whitespace-nowrap">#</th>
                        {selectedForm?.fields?.map((field, idx) => (
                          <th 
                            key={field.id} 
                            className={`text-left p-3 font-bold whitespace-nowrap ${idx < selectedForm.fields.length - 1 ? 'border-r border-green-500' : ''}`}
                          >
                            {field.label}
                          </th>
                        ))}
                        <th className="text-left p-3 font-bold border-l border-green-500 whitespace-nowrap">Submitted At</th>
                      </tr>
                    </thead>
                    <tbody>
                      {formResponses.map((response, index) => (
                        <tr 
                          key={response.id} 
                          className={`${index % 2 === 0 ? 'bg-white' : 'bg-gray-50'} hover:bg-blue-50 transition-colors`}
                        >
                          <td className="p-3 font-mono text-gray-600 border-r border-gray-200 bg-gray-100">{index + 1}</td>
                          {selectedForm?.fields?.map((field, idx) => (
                            <td 
                              key={field.id} 
                              className={`p-3 ${idx < selectedForm.fields.length - 1 ? 'border-r border-gray-200' : ''}`}
                            >
                              <span className="block max-w-xs truncate" title={response.responses?.[field.id] || '-'}>
                                {response.responses?.[field.id] || '-'}
                              </span>
                            </td>
                          ))}
                          <td className="p-3 font-mono text-xs text-gray-500 border-l border-gray-200 whitespace-nowrap">
                            {response.submitted_at && format(new Date(response.submitted_at), 'MMM dd, yyyy h:mm a')}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
                
                {/* Excel-like footer */}
                <div className="flex-shrink-0 mt-2 flex items-center justify-between text-xs text-gray-500 bg-gray-100 p-2 rounded border">
                  <span>Total: {formResponses.length} rows × {(selectedForm?.fields?.length || 0) + 2} columns</span>
                  <span>Click "Export CSV" to download as spreadsheet</span>
                </div>
              </div>
            )}
          </DialogContent>
        </Dialog>
      </main>
    </div>
  );
};

export default Forms;
